import React from 'react';
import { AssessmentIntro } from './AssessmentIntro';
import { AssessmentProfile } from './AssessmentProfile';
import AssessmentQuestions from './AssessmentQuestions';
import AssessmentContact from './AssessmentContact';
import { AssessmentResults } from './AssessmentResults';
import { ContactInfo, AssessmentAnswers } from '@/types/assessment';
import { calculateAssessmentOverallScore } from '@/utils/assessmentUtils';

interface Question {
  id: number;
  code?: string;
  question: string;
  responses: string[];
  category: string;
  type?: string;
}

interface AssessmentModalContentProps {
  currentStep: number;
  totalSteps: number;
  assessmentType: 'ia-diagnostic' | 'training-transformation';
  profileQuestions: Question[];
  categories: string[];
  questionsByCategory: Record<string, Question[]>;
  answers: AssessmentAnswers;
  contactInfo: ContactInfo;
  isSubmitting: boolean;
  questionsData: any[];
  onNext: () => void;
  onAnswerChange: (questionId: number, answer: string | string[]) => void;
  onContactSubmit: (contactData: any) => void;
  calculateScores: () => Record<string, { score: number; maxScore: number }>;
  onClose: () => void;
  getFilteredQuestionsForCurrentCategory?: () => Question[];
  axisDescriptions?: Record<string, string>;
  onContactChange: (field: string, value: string | boolean) => void;
}

export const AssessmentModalContent: React.FC<AssessmentModalContentProps> = ({
  currentStep,
  totalSteps,
  assessmentType,
  profileQuestions,
  categories,
  questionsByCategory,
  answers,
  contactInfo,
  isSubmitting,
  questionsData,
  onNext,
  onAnswerChange,
  onContactSubmit,
  calculateScores,
  onClose,
  getFilteredQuestionsForCurrentCategory,
  axisDescriptions,
  onContactChange,
}) => {
  if (currentStep === 0) {
    return <AssessmentIntro onNext={onNext} assessmentType={assessmentType} />;
  }

  if (currentStep === 1) {
    return (
      <AssessmentProfile
        questions={profileQuestions}
        answers={answers}
        onAnswerChange={onAnswerChange}
        assessmentType={assessmentType}
      />
    );
  }

  if (currentStep >= 2 && currentStep < 2 + categories.length) {
    const categoryIndex = currentStep - 2;
    const category = categories[categoryIndex];
    const categoryQuestions = getFilteredQuestionsForCurrentCategory
      ? getFilteredQuestionsForCurrentCategory()
      : questionsByCategory[category] || [];

    return (
      <AssessmentQuestions
        title={category}
        questions={categoryQuestions}
        answers={answers}
        onAnswerChange={onAnswerChange}
        assessmentType={assessmentType}
        categoryExplanation={axisDescriptions?.[category]}
      />
    );
  }

  if (currentStep === totalSteps - 2) {
    const scores = calculateScores();
    const overallScore = calculateAssessmentOverallScore(scores, assessmentType);
    const orgTypeQuestion =
      assessmentType === 'training-transformation'
        ? profileQuestions.find((question) => question.code === 'P1')
        : undefined;
    const orgTypeAnswer = orgTypeQuestion ? answers[orgTypeQuestion.id] : undefined;
    const organizationType = Array.isArray(orgTypeAnswer) ? orgTypeAnswer[0] : orgTypeAnswer;

    return (
      <AssessmentContact
        contactInfo={contactInfo as any}
        isSubmitting={isSubmitting}
        onContactChange={onContactChange}
        onSubmit={onContactSubmit}
        assessmentType={assessmentType}
        overallScore={overallScore}
        organizationType={organizationType}
      />
    );
  }

  if (currentStep === totalSteps - 1) {
    const scores = calculateScores();
    return (
      <AssessmentResults
        scores={scores}
        answers={answers}
        contactInfo={contactInfo}
        questions={questionsData}
        onClose={onClose}
        assessmentType={assessmentType}
      />
    );
  }

  return null;
};
